import { Button, Header, LoadingSpinner } from '../../Generic'
import { observer } from 'mobx-react'
import { QRCodeSVG } from 'qrcode.react'
import { useContext, useState } from 'react'
import { StoreContext } from '@store/Root.store'

import { actionReport } from '@methods/actionReport'

import '../SwitchToMobile.css'
import { YoutubeEmbed } from '@components/YoutubeEmbed/YoutubeEmbed'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { cx } from '@/utils/cx'
import { hasButtonLink } from '@/utils/themeHelpers'
import { SendSmsStatus } from './SendSmsStatus'
import { useScanQR } from './useScanQR'

interface ScanQRProps {
  nextStep: () => void
  stayUrl?: string
  smsUrl?: string
  smsMaxAttempts: number
  isSmsEnabled?: boolean
  isCameraAvailable?: boolean
  retrySeconds?: number
  getProceedLinkUrl?: string
  isMobileSessionStartedUrl?: string
  closeDesktopSessionUrl?: string
}

export const ScanQR = observer(
  ({
    nextStep,
    stayUrl,
    smsUrl,
    smsMaxAttempts,
    isSmsEnabled,
    isCameraAvailable,
    retrySeconds,
    getProceedLinkUrl,
    isMobileSessionStartedUrl,
    closeDesktopSessionUrl,
  }: ScanQRProps) => {
    const store = useContext(StoreContext)
    const { themeId } = store.InterfaceState
    const { context } = store.ScenarioState
    const { switchToMobile: trans } = store.TranslationsState.translations
    const shouldShowAccordion = isSmsEnabled || isCameraAvailable
    const instructionalVideoUrl = context?.instructionalVideoUrl

    const {
      QRCodeUrl,
      isRedirecting,
      disabled,
      counting,
      isSendSmsButtonVisible,
      sendSmsCount,
      countdownProgress,
      sendSms,
      stayOnDevice,
    } = useScanQR({
      nextStep,
      stayUrl,
      smsUrl,
      isSmsEnabled,
      retrySeconds,
      getProceedLinkUrl,
      isMobileSessionStartedUrl,
      closeDesktopSessionUrl,
    })

    const [isAccordionOpen, setIsAccordionOpen] = useState(false)
    const [isVideoAccordionOpen, setIsVideoAccordionOpen] = useState(false)
    const [auditLogSent, setAuditLogSent] = useState(false)
    const [auditLogVideoSent, setAuditLogVideoSent] = useState(false)

    const accordionOpenHandler = () => {
      if (!auditLogSent && !isAccordionOpen) {
        actionReport({
          type: 'event.onboarding-web.go-to-mobile.OTHER_CONTINUE_OPTIONS_OPENED',
          payload: {},
        })

        setAuditLogSent(true)
      }

      setIsAccordionOpen(!isAccordionOpen)
    }

    const isVideoAccordionOpenHandler = () => {
      if (!auditLogVideoSent && !isVideoAccordionOpen) {
        actionReport({
          type: 'event.onboarding-web.go-to-mobile.VIDEO_SECTION_OPENED',
          payload: {},
        })

        setAuditLogVideoSent(true)
      }

      setIsVideoAccordionOpen(!isVideoAccordionOpen)
    }

    return QRCodeUrl ? (
      <>
        <Header>{trans.scanQr.header}</Header>

        <p className="generic__intro-paragraph">{trans.scanQr.description1}</p>

        <div className="layout__margined m-0-0-24" />

        <div className="generic__paragraph-centered">
          <QRCodeSVG
            value={QRCodeUrl}
            href={QRCodeUrl}
            data-testid="qrCode"
            role="img"
            aria-label={trans.scanQr.qrCode}
            title={trans.scanQr.qrCode}
          />
        </div>

        <div className="layout__margined m-0-0-24" />

        <p className="generic__intro-paragraph">{trans.scanQr.description2}</p>

        {shouldShowAccordion && (
          <div className={cx('switch-to-mobile__accordion-wrapper')}>
            <div
              className={cx(
                'switch-to-mobile__accordion-title',
                'open-accordion',
                isAccordionOpen && 'switch-to-mobile__accordion-title--open'
              )}
              tabIndex={0}
              role="button"
              aria-expanded={isAccordionOpen}
              aria-controls="accordion-other-options"
              onClick={accordionOpenHandler}
              onKeyDown={(event) => onKeyDownHandler(event, () => accordionOpenHandler())}
            >
              <h2>
                {isSmsEnabled
                  ? trans.scanQr.otherOptionsTitle
                  : trans.scanQr.stayOnThisDeviceQuestion}
              </h2>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
              >
                <path
                  d="M1 1.28369L5 5.28369L9 1.28369"
                  stroke="#34353A"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div
              id="accordion-other-options"
              className={cx(
                'switch-to-mobile__accordion',
                isAccordionOpen && 'switch-to-mobile__accordion--open'
              )}
              aria-hidden={!isAccordionOpen}
            >
              {isSmsEnabled && (
                <>
                  <p className="generic__intro-paragraph">{trans.scanQr.sendSms}</p>

                  <SendSmsStatus
                    disabled={disabled}
                    counting={counting}
                    isRedirecting={isRedirecting}
                    isSendSmsButtonVisible={isSendSmsButtonVisible}
                    sendSmsCount={sendSmsCount}
                    smsMaxAttempts={smsMaxAttempts}
                    countdownProgress={countdownProgress}
                    sendSms={sendSms}
                    tabIndex={isAccordionOpen ? 0 : -1}
                  />
                </>
              )}

              <>
                {isSmsEnabled ?? (
                  <p className="generic__intro-paragraph">
                    {trans.scanQr.stayOnThisDeviceQuestion}
                  </p>
                )}

                <Button
                  disabled={disabled || counting || isRedirecting}
                  onClick={stayOnDevice}
                  className={cx(
                    hasButtonLink(themeId) ? 'button--link' : 'button--secondary',
                    'button--proceed'
                  )}
                  tabIndex={isAccordionOpen ? 0 : -1}
                >
                  {isRedirecting && <LoadingSpinner inButton />}
                  <span
                    className={cx(
                      'generic__button-text',
                      isRedirecting && 'generic__button-text--loading'
                    )}
                    data-testId="proceed"
                  >
                    {trans.scanQr.stayOnThisDeviceBtn}
                  </span>
                </Button>
              </>
            </div>
          </div>
        )}

        {instructionalVideoUrl && (
          <>
            <div className={cx('switch-to-mobile__accordion-wrapper')}>
              <div
                className={cx(
                  'switch-to-mobile__accordion-title',
                  'open-video-accordion',
                  isVideoAccordionOpen && 'switch-to-mobile__accordion-title--open'
                )}
                tabIndex={0}
                role="button"
                aria-expanded={isVideoAccordionOpen}
                aria-controls="accordion-video"
                onClick={isVideoAccordionOpenHandler}
                onKeyDown={(event) => onKeyDownHandler(event, () => isVideoAccordionOpenHandler())}
              >
                <h2>{trans.scanQr.showVideoTitle}</h2>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="7"
                  viewBox="0 0 10 7"
                  fill="none"
                >
                  <path
                    d="M1 1.28369L5 5.28369L9 1.28369"
                    stroke="#34353A"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              <div
                id="accordion-video"
                className={cx(
                  'switch-to-mobile__accordion',
                  isVideoAccordionOpen && 'switch-to-mobile__accordion--open'
                )}
                aria-hidden={!isVideoAccordionOpen}
              >
                <div className="generic__intro-paragraph">
                  <YoutubeEmbed
                    embedVideo={instructionalVideoUrl}
                    tabIndex={isVideoAccordionOpen ? 0 : -1}
                    title={trans.scanQr.youtubeVideoTitle}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </>
    ) : (
      <LoadingSpinner />
    )
  }
)
